import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Brand from "@/components/Brand";

// Publicly accessible Privacy Policy. Play Store requires a stable public URL
// for this — https://stylebay.co.in/privacy points here.

export default function Privacy() {
  return (
    <div
      className="min-h-screen bg-background text-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="privacy-page"
    >
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/login" data-testid="privacy-back-btn" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]">
            <ArrowLeft size={14} /> Back
          </Link>
          <Brand size="sm" />
          <span className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10 lg:py-16">
        <p className="eyebrow">Legal</p>
        <h1 className="editorial-h1 mt-3">Privacy Policy</h1>
        <p className="text-xs text-muted-foreground mt-3">Last updated: 12 February 2026</p>

        <div className="prose prose-sm dark:prose-invert mt-8 space-y-6">
          <Section title="1. Who we are">
            StyleBay (“we”, “us”) is an AI-powered fashion styling service. This policy explains what
            personal data we collect, why we collect it, and your rights under Indian data-protection law
            (DPDPA 2023) and, where applicable, the EU GDPR.
          </Section>

          <Section title="2. Data we collect">
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><b>Account data</b> — name, email, profile picture (received from Google Sign-In).</li>
              <li><b>Profile data</b> — age, gender, height, weight, body type, style preferences, bio.</li>
              <li><b>Uploaded media</b> — photos of your wardrobe, face for beauty scans, outfit photos.</li>
              <li><b>Usage data</b> — outfits generated, try-ons, chats with the AI stylist, quotas.</li>
              <li><b>Device data</b> — Android device model, OS version, app version, crash logs.</li>
              <li><b>Payment metadata</b> — Razorpay order and payment IDs (we never see your card details).</li>
              <li><b>Ad identifiers</b> — Google Advertising ID for free-tier ad personalisation, per your Android settings.</li>
            </ul>
          </Section>

          <Section title="3. How we use it">
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Personalise outfits, try-ons, hair/beard/makeup previews and shopping recommendations.</li>
              <li>Send you routine reminders and (with consent) push notifications.</li>
              <li>Process subscriptions and prevent payment fraud.</li>
              <li>Serve ads to free-tier users via Google AdMob.</li>
              <li>Debug crashes, monitor abuse, and improve model quality.</li>
            </ul>
          </Section>

          <Section title="4. Third-party processors">
            We share limited data with the following processors, all under written agreements:
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><b>Google</b> — Sign-In, Play Services, AdMob, Gemini Vision (image analysis + Nano Banana try-on generation).</li>
              <li><b>Anthropic / OpenAI</b> — text generation (styling advice, chat).</li>
              <li><b>Razorpay</b> — payment processing for subscriptions.</li>
              <li><b>Emergent Object Storage</b> — encrypted image hosting.</li>
              <li><b>MongoDB Atlas</b> — encrypted database hosting (data-at-rest AES-256).</li>
            </ul>
          </Section>

          <Section title="5. AI &amp; your photos">
            When you upload photos for wardrobe tagging, virtual try-on or beauty scans, the images are
            sent to our AI vision providers for the sole purpose of generating your requested output.
            Providers do not retain or train on your photos beyond the immediate request. You can enable
            <b> Auto-delete scan photos</b> in Settings → Privacy to automatically purge uploads older
            than your chosen retention window.
          </Section>

          <Section title="6. Retention">
            We keep account data for as long as your account exists. Beauty scans and try-on images
            respect your Auto-delete setting. Payment records are retained for 8 years as required by
            Indian tax law. Anonymised aggregate analytics may be retained indefinitely.
          </Section>

          <Section title="7. Your rights">
            You may (a) access, correct or export your data; (b) delete your account at any time from
            Profile → Settings → Delete Account; (c) withdraw consent for optional processing;
            (d) lodge a complaint with the Data Protection Board of India. Email <a className="underline" href="mailto:contact@stylebay.co.in">contact@stylebay.co.in</a> to exercise any right.
          </Section>

          <Section title="8. Children">
            The Service is not intended for children under 13. We do not knowingly collect data from
            children under 13. If you believe we have, contact us and we will delete it.
          </Section>

          <Section title="9. Security">
            We use HTTPS everywhere, encrypt data at rest, use httpOnly session cookies, and rotate
            secrets regularly. No system is 100% secure — please use a strong Google password and enable
            2-factor authentication on your Google account.
          </Section>

          <Section title="10. International transfers">
            Some of our processors (Google, Anthropic, OpenAI) operate globally. Where required, transfers
            outside India are protected by standard contractual clauses or equivalent safeguards.
          </Section>

          <Section title="11. Changes">
            We will notify you in-app or by email at least 7 days before material changes take effect.
          </Section>

          <Section title="12. Contact">
            Data controller: StyleBay · <a className="underline" href="mailto:contact@stylebay.co.in">contact@stylebay.co.in</a><br />
            Address: Gunour, Panna, MP, India
          </Section>

          <Section title="13. Grievance Officer (India IT Rules 2021)">
            <div>
              <b>Name:</b> Ram Soni<br />
              <b>Email:</b> <a className="underline" href="mailto:contact@stylebay.co.in">contact@stylebay.co.in</a><br />
              <b>Address:</b> Gunour, Panna, MP, India
            </div>
            <p className="mt-2">
              Complaints acknowledged within 24 h and resolved within 15 d per IT Rules 2021 §3(2).
              Submit at <Link to="/grievance" className="underline">stylebay.co.in/grievance</Link>.
            </p>
          </Section>
        </div>

        <p className="text-[10px] text-muted-foreground mt-12 uppercase tracking-[0.18em]">
          © 2026 StyleBay · <Link to="/terms" className="underline">Terms &amp; Conditions</Link>
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-xl tracking-tight">{title}</h2>
      <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{children}</div>
    </section>
  );
}
